// components/Vehicle/AddVehicleModal.jsx
import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useAddVehicleAndAssignToDriverMutation } from "store/services/vehicleApi";
import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static";

const INITIAL_STATE = {
    registration_number: "",
    vehicleType: "",
    capacity: "",
    unit: "Ton",
    maintenance_due_date: "",
    ownership_type: "",
};

/**
 * Props:
 *   open          boolean
 *   onClose       () => void
 *   onSuccess     (newVehicle) => void   — called with the created vehicle object
 *   driverId      string                 — driver to assign the vehicle to
 *   accountId     string
 *   entityId      string
 */
const AddVehicleModal = ({ open, onClose, onSuccess, driverId, accountId, entityId }) => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({ defaultValues: INITIAL_STATE });

    const [addVehicleAndAssignToDriver, { isLoading }] =
        useAddVehicleAndAssignToDriverMutation();

    const { data: vehicleTypes = [], isLoading: loadingVehicleTypes } = useListListQuery({
        type: ENTITY_LIST_CONSTANTS.VEHICLE_TYPES,
        status: "Active",
    });
    const mappedVehicleTypes = useMemo(
        () =>
            Array.isArray(vehicleTypes)
                ? vehicleTypes.map((item) => ({ label: item?.name, value: item?._id }))
                : [],
        [vehicleTypes]
    );

    const { data: ownershipTypes = [], isLoading: loadingOwnershipTypes } = useListListQuery({
        type: ENTITY_LIST_CONSTANTS.VEHICLE_OWNERSHIP_TYPES,
        status: "Active",
    });
    const mappedOwnershipTypes = useMemo(
        () =>
            Array.isArray(ownershipTypes)
                ? ownershipTypes.map((item) => ({ label: item?.name, value: item?._id }))
                : [],
        [ownershipTypes]
    );

    const handleClose = () => {
        if (isLoading) return;
        reset(INITIAL_STATE);
        onClose();
    };

    const onSubmit = async (formData) => {
        const payload = {
            ...formData,
            driverId,
            account: accountId,
            entity: entityId,
            status: true,
        };

        const result = await addVehicleAndAssignToDriver(payload);

        if (result?.data?._id) {
            reset(INITIAL_STATE);
            onSuccess(result.data);
        }
        // error toast is handled in the parent via onSuccess not being called
        // or you can add a local error state here if you prefer isolated error handling
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={isLoading}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pb: 1,
                }}
            >
                <Typography variant="h6" fontWeight={600}>
                    Add New Vehicle
                </Typography>
                <IconButton onClick={handleClose} disabled={isLoading} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2} sx={{ pt: 1 }}>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="registration_number"
                            control={control}
                            rules={{ required: "Registration number is required." }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    required
                                    fullWidth
                                    size="small"
                                    label="Registration Number"
                                    error={!!errors.registration_number}
                                    helperText={errors?.registration_number?.message}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="vehicleType"
                            control={control}
                            rules={{
                                required: "Vehicle type is required.",
                                validate: (value) =>
                                    mappedVehicleTypes.some((opt) => opt.value === value) ||
                                    "Invalid vehicle type.",
                            }}
                            render={({ field }) => (
                                <BaseSelect
                                    {...field}
                                    required
                                    fullWidth
                                    size="small"
                                    label="Vehicle Type"
                                    options={mappedVehicleTypes}
                                    getOptionLabel={(opt) => opt.label || ""}
                                    getOptionValue={(opt) => opt.value || ""}
                                    error={!!errors.vehicleType}
                                    helperText={errors?.vehicleType?.message}
                                    loading={loadingVehicleTypes}
                                    loadingText="Loading Vehicle Types..."
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="capacity"
                            control={control}
                            rules={{
                                required: "Capacity is required.",
                                min: { value: 1, message: "Capacity must be at least 1." },
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    required
                                    fullWidth
                                    size="small"
                                    label="Capacity"
                                    type="number"
                                    error={!!errors.capacity}
                                    helperText={errors?.capacity?.message}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="unit"
                            control={control}
                            rules={{ required: "Unit is required." }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    required
                                    select
                                    fullWidth
                                    size="small"
                                    label="Unit"
                                    error={!!errors.unit}
                                    helperText={errors?.unit?.message}
                                >
                                    <MenuItem value="Ton">Ton</MenuItem>
                                    <MenuItem value="Unit">Unit</MenuItem>
                                </TextField>
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="ownership_type"
                            control={control}
                            rules={{
                                required: "Ownership type is required.",
                                validate: (value) =>
                                    mappedOwnershipTypes.some((opt) => opt.value === value) ||
                                    "Invalid ownership type.",
                            }}
                            render={({ field }) => (
                                <BaseSelect
                                    {...field}
                                    required
                                    fullWidth
                                    size="small"
                                    label="Ownership Type"
                                    options={mappedOwnershipTypes}
                                    getOptionLabel={(opt) => opt.label || ""}
                                    getOptionValue={(opt) => opt.value || ""}
                                    error={!!errors.ownership_type}
                                    helperText={errors?.ownership_type?.message}
                                    loading={loadingOwnershipTypes}
                                    loadingText="Loading Ownership Types..."
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="maintenance_due_date"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    size="small"
                                    label="Maintenance Due Date (Optional)"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                />
                            )}
                        />
                    </Grid>

                </Grid>
            </DialogContent>

            {/* Actions */}
            <Grid
                container
                justifyContent="flex-end"
                spacing={1}
                sx={{ px: 3, py: 2 }}
            >
                <Grid item>
                    <Button onClick={handleClose} disabled={isLoading}>
                        Cancel
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isLoading}
                    >
                        {isLoading ? "Adding..." : "Add Vehicle"}
                    </Button>
                </Grid>
            </Grid>
        </Dialog>
    );
};

export default AddVehicleModal;